/**
 * Quick Attendance Wizard Types
 * Types for the multi-step attendance wizard
 *
 * @version 2025.1.0
 */

import { ReceptionAppointment, AttendanceStatus, AttendanceType } from '../../../../types/reception';
import { TriageData } from '../../../../types/triage';

/**
 * Wizard step identifiers
 */
export type WizardStep = 'confirmation' | 'triage' | 'start';

/**
 * Step configuration
 */
export interface StepConfig {
  key: WizardStep;
  label: string;
  optional: boolean;
}

/**
 * All possible steps configuration
 */
export const WIZARD_STEPS: StepConfig[] = [
  { key: 'confirmation', label: 'Confirmação', optional: false },
  { key: 'triage', label: 'Triagem', optional: true },
  { key: 'start', label: 'Iniciar', optional: false },
];

/**
 * Get applicable steps based on appointment status
 */
export function getApplicableSteps(status: AttendanceStatus): StepConfig[] {
  if (status === AttendanceStatus.CHECKED_IN) {
    // Skip confirmation step if already checked in
    return WIZARD_STEPS.filter((step) => step.key !== 'confirmation');
  }
  return WIZARD_STEPS;
}

/**
 * Props for QuickAttendanceWizard component
 */
export interface QuickAttendanceWizardProps {
  /** Whether the dialog is open */
  open: boolean;
  /** The appointment to process */
  appointment: ReceptionAppointment | null;
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when wizard completes successfully */
  onComplete: () => void;
  /** Check-in function from useReception */
  checkIn: (appointmentId: number) => Promise<boolean>;
  /** Start attendance function from useReception */
  startAttendance: (appointmentId: number) => Promise<boolean>;
  /** Complete attendance function from useReception (optional for backward compatibility) */
  completeAttendance?: (appointmentId: number, attendanceType?: AttendanceType) => Promise<boolean>;
}

/**
 * Wizard internal state
 */
export interface WizardState {
  /** Current step index */
  activeStep: number;
  /** Applicable steps for current appointment */
  steps: StepConfig[];
  /** Loading state during async operations */
  loading: boolean;
  /** Error message if operation failed */
  error: string | null;
  /** Collected triage data */
  triageData: TriageData;
  /** Whether confirmation checkbox is checked */
  confirmed: boolean;
  /** Whether check-in was performed in this session */
  checkInPerformed: boolean;
  /** Assigned queue number after check-in */
  assignedQueueNumber: number | null;
}

/**
 * Props for ConfirmationStep
 */
export interface ConfirmationStepProps {
  appointment: ReceptionAppointment;
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  needsCheckIn: boolean;
}

/**
 * Props for TriageStep
 */
export interface TriageStepProps {
  triageData: TriageData;
  onTriageDataChange: (data: TriageData) => void;
  onSkip: () => void;
}

/**
 * Props for StartAttendanceStep
 */
export interface StartAttendanceStepProps {
  appointment: ReceptionAppointment;
  triageData: TriageData;
  queueNumber: number | null;
  checkInPerformed: boolean;
}

/**
 * Return type for useQuickAttendance hook
 */
export interface UseQuickAttendanceReturn {
  // State
  state: WizardState;

  // Step navigation
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepKey: WizardStep;

  // Actions
  handleNext: () => Promise<void>;
  handleBack: () => void;
  handleSkipTriage: () => void;
  setConfirmed: (confirmed: boolean) => void;
  setTriageData: (data: TriageData) => void;
  reset: () => void;
}
